import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { fetchDataCategory, deleteDataCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function DataCategoryDetailPage() {
  const { businessDomainId, businessUnitId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/data-categories/${categoryId}`;
  const listPath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/data-categories`;

  const loadData = useCallback(async () => {
    try { setData(await fetchDataCategory(categoryId)); }
    catch (e) { notify.apiError(e); }
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteDataCategory(categoryId);
      notify.success(t("message.delete.success"));
      navigate(listPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("dataCategory.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.name")} value={data.dataCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.dataTypeName")} value={data.dataTypeName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.url")} value={data.url} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.description")} value={data.dataCategoryDescription} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.customerCategoryName")} value={data.customerCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.productCategoryName")} value={data.productCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.memberCategoryName")} value={data.memberCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.partnerCategoryName")} value={data.partnerCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.applicationCategoryName")} value={data.applicationCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.itCategoryName")} value={data.itCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.financialAssetCategoryName")} value={data.financialAssetCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.placeCategoryName")} value={data.placeCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.departmentName")} value={data.departmentName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("dataCategory.businessState")}
              value={data.businessState ? t(`businessState.${data.businessState}`) : ""} />
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<AssessmentIcon />}
          onClick={() => navigate(`${basePath}/classification-criteria`)}>
          {t("dataCategory.classificationCriteriaValues")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/data-categories`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
